/**
 * Business Type Service
 * 
 * Service for fetching and managing business claim types from the backend.
 * Integrates with the backend's business-relevant claim types API endpoint.
 * 
 * Evidence: Backend provides /api/v1/group/defaults/getClaimsType with business filtering
 * API Documentation: /api/v1/group/defaults/getClaimsType?businessRelevantOnly=true returns 8 business types
 */

import { apiClient } from '../utils/apiClient';
import { BUSINESS_TYPE_FIELD_MAPPINGS } from '../constants/businessTypeFieldMappings';

/**
 * Fetch business claim types from the backend
 * @returns {Promise<Array>} Array of business claim type objects from backend
 * @throws {Error} If API call fails
 */
const fetchBusinessClaimTypes = async () => {
  try {
    const response = await apiClient.get('defaults/getClaimsType?businessRelevantOnly=true');
    
    // Check if response.data is the array we need
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Check if it's wrapped in a data property
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    // Check if it's double-wrapped
    if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
      return response.data.data.data;
    }
    
    // Return empty array as fallback
    return [];
    
  } catch (error) {
    console.error('BusinessTypeService Error:', error.message);
    return [];
  }
};

/**
 * Get field requirements for a specific business type
 * @param {string} businessType - Business claim type code (DTH, 010, 012, etc.)
 * @returns {Array} Array of required field names for the business type
 */
const getFieldRequirements = (businessType) => {
  return BUSINESS_TYPE_FIELD_MAPPINGS[businessType] || [];
};

/**
 * Get business type metadata by code
 * @param {string} typeCode - Business claim type code
 * @returns {Promise<Object>} Business type metadata
 * @throws {Error} If API call fails
 */
const getBusinessTypeMetadata = async (typeCode) => {
  // TODO: Check if backend has individual claim type endpoint
  // For now, fetch all types and filter
  const allTypes = await fetchBusinessClaimTypes();
  return allTypes.find(type => type.typeCode === typeCode || type.value === typeCode);
};

/**
 * Validate business type fields against backend business rules
 * @param {Object} data - Claim data to validate
 * @returns {Promise<Object>} Validation result from business rules engine
 * @throws {Error} If API call fails
 */
const validateBusinessTypeFields = async (data) => {
  const response = await apiClient.post('claims/validate-rules', data);
  return response.data;
};

const businessTypeService = {
  fetchBusinessClaimTypes,
  getFieldRequirements,
  getBusinessTypeMetadata,
  validateBusinessTypeFields
};

export default businessTypeService;